/**
 * useMediaDetails — feature hook for `MediaDetailsPage` (`/chat/media/:id`).
 *
 * Owns the business logic for the directly-routable chat-media viewer:
 *  - The arg-missing anti-leak gate (spec point 7 / consideration B): the chat
 *    message and the peer's public JWK arrive ONLY as route args. When they are
 *    missing (a pasted / shared URL has no `location.state`) the hook surfaces an
 *    error and deliberately NEVER fetches the message by id — a shared URL must
 *    not be usable to retrieve subscriber-gated media.
 *  - The IMAGE branch: read the encrypted full-image ciphertext's BUCKET-RELATIVE
 *    object path from the chat message (`message.relativeServerPath`) and hand it
 *    — together with the route-arg peer JWK (read on the page) — to
 *    `EncryptedCachedImage`, which resolves the tokenized download URL via the
 *    domain `ResolveStorageDownloadUrlUseCase` (Finding 5) and owns the fetch →
 *    `DecryptBinaryUseCase` → `Blob` → `blob:` hop AND a
 *    module-level decrypted-blob LRU. The hook no longer fetches/decrypts or owns
 *    a page-local `blob:` URL: `MediaDetailsPage` is only reachable from the open
 *    chat (`Navigator.navigateToMediaDetails` route args), where the same bytes
 *    were already decrypted+cached as a thumbnail, so going through the shared
 *    cache is the intended behaviour (the LRU owns + revokes the blob on eviction).
 *
 * The VIDEO branch (Task 15): call `InitializeChatVideoUseCase` (Task 12) with
 * the chat message `documentId`, the peer id, and the current user's `uid`/`iat`
 * (derived the same way `videoSlice.initializeVideo` does, via
 * `firebaseUser.getIdTokenResult()`). On success the hook exposes the chat Media
 * CDN `videoUrl` + the decrypted `VideoDecryptionKeys` on the `video`
 * discriminator so the page can mount `InlineVideoPlayer` (encrypted-HLS, native
 * controls). On failure (no active subscription / permission denied) the chat
 * callable returns null and the use case surfaces `{ success: false }`, which
 * the hook turns into the `videoError` page error — no video mounts.
 *
 * Page + feature-hook split (see `presentation.md`): the page owns `useBasePage`,
 * the `screenKey` mint, `useParams`, the `location.state` snapshot, and the auth
 * gate; this hook owns state + side-effects and exposes memoized lifecycle
 * callbacks the page bridges into `useBasePage`. It also reports its own
 * `isFirstLoad`.
 *
 * Single-key-slot serialization caveat: `textEncryptionService` has ONE derived
 * AES-GCM key slot shared across the text + binary paths
 * (`InitializeTextEncryptionUseCase` JSDoc). The IMAGE branch decrypts via
 * `EncryptedCachedImage`, which re-derives the SAME single conversation peer key
 * idempotently — the page opens for exactly one peer (the route-arg peer key),
 * so routing the full image through the shared cache clobbers nothing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { MediaType } from '@/domain/enums/mediaType';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { VideoDecryptionKeys } from '@/domain/entities/video/videoDecryptionEntity';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { createInitializeChatVideoUseCase } from '@/domain/usecases/chatMedia/initializeChatVideoUseCase';
import { getAuthFirebaseUserUseCase } from '@/domain/usecases/user/getAuthFirebaseUserUseCase';
import { useLocale } from '@/presentation/contexts/LocaleContext';

/** Coarse lifecycle token mirrored onto the page root's `data-qa-status`. */
export type MediaDetailsStatus = 'loading' | 'ready' | 'error';

/**
 * Typed media discriminator. The IMAGE branch resolves the encrypted full-image
 * ciphertext's BUCKET-RELATIVE object path (the page renders it through
 * `EncryptedCachedImage`, which resolves the tokenized download URL via the
 * domain UseCase, then does the fetch+decrypt+cache — Finding 5); the VIDEO
 * branch resolves the chat Media CDN `videoUrl` + the decrypted
 * `VideoDecryptionKeys` the page feeds to `InlineVideoPlayer` (which builds the
 * encrypted hls.js loader via `createEncryptedLoader`).
 */
export type ResolvedMedia =
  | { kind: MediaType.IMAGE; fullImagePath: string }
  | { kind: MediaType.VIDEO; videoUrl: string; decryptionKeys: VideoDecryptionKeys };

/**
 * Route args for `MediaDetailsPage`, carried via `location.state`. The page
 * snapshots `location.state` once and passes it here. `null` means the page was
 * opened without args (pasted / shared URL) — the anti-leak gate fires.
 */
export interface MediaDetailsRouteArgs {
  /** The chat message to render (image / video). */
  message: ChatMessageEntity;
  /**
   * Peer's ECDH public JWK (from the open chat's `peerUser.publicJwk`). Drives
   * the chat-key derivation before the image decrypt. `null` when the peer had
   * no published key — the decrypt then fails and surfaces the image error.
   */
  peerPublicJwk: JsonWebKey | null;
}

export interface UseMediaDetailsParams {
  /** The `:id` URL param (the chat message `documentId`). Used only to confirm
   *  the route args belong to this URL — never dereferenced to fetch by id. */
  documentId: string | undefined;
  /** Route-args snapshot (`location.state`). `null` on the pasted-URL path. */
  routeArgs: MediaDetailsRouteArgs | null;
}

export interface UseMediaDetailsResult {
  /**
   * Resolved media: the IMAGE variant carries the encrypted full-image
   * ciphertext URL (rendered through `EncryptedCachedImage`), the VIDEO variant
   * carries the CDN `videoUrl` + decrypted keys. `null` until ready / on error.
   */
  media: ResolvedMedia | null;
  /** Coarse status for the page root's `data-qa-status`. */
  status: MediaDetailsStatus;
  /** True until the resolution settles. Gates the page spinner. */
  isFirstLoad: boolean;
  /** Localized user-visible error message when `status === 'error'`, else null. */
  errorMessage: string | null;
  /** Lifecycle bridge — the page passes this into `useBasePage({ onInit })`. */
  onPageInit: () => void;
  /**
   * Image-failure bridge — the page wires this to `EncryptedCachedImage`'s
   * `onError`. The component reports any fetch / decrypt / decode failure for
   * the full image (it owns the fetch+decrypt+cache + the symmetric `<img>`
   * decode-failure guard); the hook maps that to the page's `imageError` state.
   */
  onImageError: () => void;
}

export const useMediaDetails = (
  params: UseMediaDetailsParams,
): UseMediaDetailsResult => {
  const { documentId, routeArgs } = params;
  const { l10n } = useLocale();

  const [media, setMedia] = useState<ResolvedMedia | null>(null);
  const [status, setStatus] = useState<MediaDetailsStatus>('loading');
  const [isFirstLoad, setIsFirstLoad] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Guards against double-run of the one-shot load (React 18 StrictMode mounts
  // effects twice in dev; `useBasePage.onInit` is itself once-only, but this
  // makes the load idempotent regardless of how the page bridges it).
  const hasLoadedRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Resolve the encrypted full-image ciphertext's BUCKET-RELATIVE object path
   * from the message and expose it on the IMAGE discriminant. The download-URL
   * resolve → fetch → decrypt → `blob:` hop (and the shared decrypted-blob LRU)
   * is owned by `EncryptedCachedImage` on the page, not here; this only reads the
   * relative path and surfaces the image-error state when the message carries no
   * `relativeServerPath`.
   *
   * The relative path is passed straight to `EncryptedCachedImage` (Finding 5):
   * the old `ChatUtils.getChatMessageServerPath` concatenation
   * (`DEFAULT_BUCKET_PREFIX + relativeServerPath`) produced an un-encoded,
   * token-less URL the Storage REST endpoint rejected with HTTP 400.
   */
  const loadImage = useCallback(
    (args: MediaDetailsRouteArgs): void => {
      const fullImagePath = args.message.relativeServerPath;
      if (!fullImagePath) {
        // No full-image path on the message — treat as an image error rather
        // than a never-resolving spinner.
        setStatus('error');
        setErrorMessage(l10n.chat.mediaDetails.imageError);
        return;
      }

      // The page mounts `EncryptedCachedImage` with this RELATIVE path + the
      // route-arg peer JWK; it resolves the tokenized download URL via the domain
      // UseCase and owns the fetch+decrypt+cache, reporting any failure via
      // `onImageError`. The image branch deliberately never touches
      // `isFirstLoad` — there is no async work in the hook (reading the relative
      // path is synchronous), so the page must not paint its own spinner;
      // `EncryptedCachedImage` owns the loading placeholder.
      setMedia({ kind: MediaType.IMAGE, fullImagePath });
      setStatus('ready');
      setErrorMessage(null);
    },
    [l10n.chat.mediaDetails.imageError],
  );

  /**
   * Initialize the chat video: derive the current user's `uid`/`iat` the SAME
   * way `videoSlice.initializeVideo` does (`firebaseUser.getIdTokenResult()`),
   * resolve the chat counterparty as the peer id, then call
   * `InitializeChatVideoUseCase` (Task 12). On success expose the chat Media CDN
   * `videoUrl` + decrypted keys for `InlineVideoPlayer`. On failure (no active
   * subscription / permission denied → null callable result) surface the video
   * error rather than a stuck spinner.
   */
  const loadVideo = useCallback(
    async (args: MediaDetailsRouteArgs): Promise<void> => {
      // Video is the only branch with real async work in the hook
      // (`getIdTokenResult()` + the `InitializeChatVideoUseCase` callable), so
      // it owns the page spinner: flip `isFirstLoad` true before the await work
      // and clear it on both the success and catch paths below.
      setIsFirstLoad(true);
      try {
        const firebaseUser = getAuthFirebaseUserUseCase.execute();
        if (!firebaseUser) {
          throw new Error('No authenticated user for chat video init');
        }

        // Same uid/iat derivation as `videoSlice.initializeVideo`.
        const idTokenResult = await firebaseUser.getIdTokenResult();
        const iat = Math.floor(
          new Date(idTokenResult.issuedAtTime).getTime() / 1000,
        );
        const uid = firebaseUser.uid;

        // Chat counterparty: the message's other participant relative to me.
        const peerId =
          args.message.idFrom === uid
            ? args.message.idTo
            : args.message.idFrom;

        const result = await createInitializeChatVideoUseCase().execute({
          documentId: args.message.documentId,
          peerId,
          uid,
          iat,
        });

        if (
          !result.success ||
          !result.videoUrl ||
          !result.decryptionEntity
        ) {
          throw result.error ?? new Error('Failed to initialize chat video');
        }

        if (!isMountedRef.current) return;

        setMedia({
          kind: MediaType.VIDEO,
          videoUrl: result.videoUrl,
          decryptionKeys: result.decryptionEntity.keys,
        });
        setStatus('ready');
        setErrorMessage(null);
        setIsFirstLoad(false);
      } catch (error) {
        appLogger.error(
          'Failed to initialize chat video',
          'useMediaDetails',
          { error: error as Error, context: { documentId } },
        );
        if (!isMountedRef.current) return;
        setStatus('error');
        setErrorMessage(l10n.chat.mediaDetails.videoError);
        setIsFirstLoad(false);
      }
    },
    [documentId, l10n.chat.mediaDetails.videoError],
  );

  /**
   * One-shot load, bridged from the page's `useBasePage({ onInit })`. The
   * arg-missing gate runs FIRST and short-circuits before any network work, so
   * a pasted / shared URL can never trigger a by-id fetch (anti-leak).
   */
  const onPageInit = useCallback((): void => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    // Anti-leak gate (spec point 7 / consideration B): require the route args
    // (the message + peer key) AND require the route arg's message to match the
    // URL `documentId`. No args (or a mismatched id) → error, NO by-id fetch.
    if (
      !routeArgs ||
      !routeArgs.message ||
      routeArgs.message.documentId !== documentId
    ) {
      setStatus('error');
      setErrorMessage(l10n.chat.mediaDetails.argsMissingError);
      setIsFirstLoad(false);
      return;
    }

    const messageType = routeArgs.message.type;
    if (messageType === ChatMessageType.image) {
      loadImage(routeArgs);
      return;
    }

    if (messageType === ChatMessageType.video) {
      // Video branch: initialize the encrypted chat video (Task 12) and expose
      // `videoUrl` + decrypted keys for `InlineVideoPlayer`.
      void loadVideo(routeArgs);
      return;
    }

    // Any non-media type reaching this page is a routing bug — surface the
    // generic image/media error rather than a stuck spinner.
    setStatus('error');
    setErrorMessage(l10n.chat.mediaDetails.imageError);
    setIsFirstLoad(false);
  }, [
    routeArgs,
    documentId,
    loadImage,
    loadVideo,
    l10n.chat.mediaDetails.argsMissingError,
    l10n.chat.mediaDetails.imageError,
  ]);

  /**
   * Full-image-failure bridge — wired to `EncryptedCachedImage`'s `onError`.
   * The component owns the fetch / decrypt / decode lifecycle (and already logs
   * the specific failure), so this just flips the page to the `imageError`
   * state. Logging here would double-report the failure the component logged.
   */
  const onImageError = useCallback((): void => {
    if (!isMountedRef.current) return;
    setStatus('error');
    setErrorMessage(l10n.chat.mediaDetails.imageError);
  }, [l10n.chat.mediaDetails.imageError]);

  return useMemo<UseMediaDetailsResult>(
    () => ({
      media,
      status,
      isFirstLoad,
      errorMessage,
      onPageInit,
      onImageError,
    }),
    [media, status, isFirstLoad, errorMessage, onPageInit, onImageError],
  );
};
